import React from 'react';
import './ResultsScreen.css';

function ResultsScreen({ results, onBack, onRetake, onViewAllResults }) {
  if (!results) {
    return (
      <div className="container">
        <div className="header">
          <h1>Results</h1>
        </div>
        <div className="content">
          <div className="error">No results available</div>
          <button className="btn btn-primary" onClick={onBack}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const {
    type,
    attempt,
    totalQuestions,
    answeredCount
  } = results;

  const percentage = attempt?.score || 0;
  const correctAnswers = attempt?.correct_answers || 0;
  const totalQuestionCount = attempt?.total_questions || totalQuestions;

  const getPerformanceLevel = (score) => {
    if (score >= 90) return { level: 'Excellent', color: '#4caf50', emoji: '🌟' };
    if (score >= 80) return { level: 'Very Good', color: '#8bc34a', emoji: '👏' };
    if (score >= 70) return { level: 'Good', color: '#2196f3', emoji: '👍' };
    if (score >= 60) return { level: 'Average', color: '#ff9800', emoji: '📈' };
    return { level: 'Needs Improvement', color: '#f44336', emoji: '💪' };
  };

  const performance = getPerformanceLevel(percentage);

  return (
    <div className="container results-screen">
      <div className="header">
        <h1>
          {type === 'practice' ? 'Practice Session' : type === 'mini-mock' ? 'Mini Mock Exam' : 'Full Mock Exam'} Results
        </h1>
      </div>

      <div className="content">
        <div className="results-hero">
          <div className="performance-badge" style={{ borderColor: performance.color }}>
            <div className="emoji">{performance.emoji}</div>
            <div className="performance-level" style={{ color: performance.color }}>
              {performance.level}
            </div>
            <div className="score-large">{Math.round(percentage)}%</div>
          </div>
        </div>

        <div className="results-details">
          <div className="detail-card">
            <div className="detail-label">Correct Answers</div>
            <div className="detail-value">{correctAnswers} / {totalQuestionCount}</div>
          </div>
          <div className="detail-card">
            <div className="detail-label">Answered</div>
            <div className="detail-value">{answeredCount} / {totalQuestionCount}</div>
          </div>
          <div className="detail-card">
            <div className="detail-label">Success Rate</div>
            <div className="detail-value">{totalQuestionCount > 0 ? Math.round((correctAnswers / totalQuestionCount) * 100) : 0}%</div>
          </div>
        </div>

        <div className="progress-section">
          <h3>Overall Performance</h3>
          <div className="progress-bar-large">
            <div
              className="progress-fill"
              style={{
                width: `${percentage}%`,
                background: `linear-gradient(90deg, ${performance.color} 0%, ${performance.color}cc 100%)`
              }}
            ></div>
          </div>
          <div className="progress-labels">
            <span>0%</span>
            <span>{Math.round(percentage)}%</span>
            <span>100%</span>
          </div>
        </div>

        {attempt?.feedback_summary ? (
          <div className="feedback-box">
            <h3>Your Personalised Feedback</h3>
            {attempt.feedback_summary.split('\n\n').map((para, idx) => (
              <p key={idx}>{para}</p>
            ))}
          </div>
        ) : (
          <div className="feedback-box">
            <h3>Performance Feedback</h3>
            {percentage >= 80 ? (
              <p>🎉 Great work! You're performing very well. Keep practicing to maintain your excellent performance.</p>
            ) : percentage >= 60 ? (
              <p>📚 Good effort! Focus on reviewing the questions you got wrong and practice similar topics to improve further.</p>
            ) : (
              <p>💡 Keep studying! Review all the questions and focus on understanding the key concepts. Practice is essential for improvement.</p>
            )}
          </div>
        )}

        {Array.isArray(attempt?.topic_breakdown) && attempt.topic_breakdown.length > 0 ? (
          <div className="recommendations">
            <h3>Breakdown by Topic</h3>
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr><th>Section</th><th>Topic</th><th>Correct</th><th>Accuracy</th></tr>
                </thead>
                <tbody>
                  {attempt.topic_breakdown
                    .slice()
                    .sort((a, b) => a.pct - b.pct)
                    .map((t, idx) => (
                      <tr key={idx}>
                        <td>{t.section}</td>
                        <td>{t.topicName}</td>
                        <td>{t.correct} / {t.total}</td>
                        <td>{Math.round(t.pct)}%</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="recommendations">
            <h3>Next Steps</h3>
            <ul>
              <li>Review all incorrect answers and their explanations</li>
              <li>Focus on your weaker sections with targeted practice</li>
              <li>Take another mock exam to track your progress</li>
              <li>Create a study plan based on your weak areas</li>
            </ul>
          </div>
        )}

        <div className="results-actions">
          <button className="btn btn-secondary" onClick={onViewAllResults}>
            View All Results
          </button>
          <button className="btn btn-secondary" onClick={onRetake}>
            Retake
          </button>
          <button className="btn btn-primary btn-lg" onClick={onBack}>
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

export default ResultsScreen;
